/**
 * Tablature, as MusicXML says it.
 *
 * Two places carry it. The staff says what its lines are -- `<staff-details>` with one
 * `<staff-tuning>` per line, counted from the bottom line up, which is the reverse of the
 * string numbers -- and each note says where it was taken, as `<string>` and `<fret>`
 * inside `<technical>`. The pitch is written as it always is: a tab staff prints no
 * pitches, but the file still carries them, which is what lets the same part be opened as
 * notation elsewhere.
 *
 * `<technical>` is built here rather than in `export-notations.ts` because a note may want
 * more than one thing in it -- a fret and the circle of an open drum are both technical
 * marks -- and MusicXML allows only one `<technical>` on a note.
 */

import type { NotePitch } from "../document/pitch";
import type { StringTuning } from "../document/tab";
import { tuningLine } from "../document/tab";
import { el, leaf } from "../xml/write";
import type { XmlNode } from "../xml/write";

/**
 * A staff drawn with other than five lines, or tuned: the single line of a woodblock, the
 * six strings of a guitar.
 *
 * A five-line staff with nothing said about its strings needs no element at all, which is
 * every staff of ordinary music.
 */
export function staffDetailsNode(
  lines: number,
  staff: number | null,
  tuning: readonly StringTuning[] | null,
): XmlNode | null {
  if (lines === 5 && tuning === null) return null;
  return el("staff-details", { number: staff }, [
    leaf("staff-lines", lines),
    ...(tuning === null ? [] : tuningNodes(tuning)),
  ]);
}

/** One `<staff-tuning>` per string, written from the bottom line of the staff upwards. */
function tuningNodes(tuning: readonly StringTuning[]): XmlNode[] {
  return tuning
    .map((open, index) => ({ open, line: tuningLine(tuning, index + 1) }))
    .sort((a, b) => a.line - b.line)
    .map(({ open, line }) =>
      el("staff-tuning", { line }, [
        leaf("tuning-step", open.step),
        open.alter === 0 ? null : leaf("tuning-alter", open.alter),
        leaf("tuning-octave", open.octave),
      ]),
    );
}

/**
 * The `<technical>` marks of one note, or nothing when it has none.
 *
 * `<open-string/>` rather than MusicXML 4.0's own `<open/>` for a drum left to ring: the
 * two draw the same circle, and this is the one every drum file written before 4.0 carries
 * and every reader knows. `harmonic` is a string's natural harmonic, the note written being
 * the one that sounds.
 */
export function technicalNode(
  pitch: NotePitch | null,
  open: boolean,
  harmonic: boolean,
): XmlNode | null {
  const children: XmlNode[] = [];
  if (pitch?.tab != null) {
    children.push(leaf("string", pitch.tab.string), leaf("fret", pitch.tab.fret));
  }
  if (open) children.push(el("open-string"));
  if (harmonic) children.push(el("harmonic", {}, [el("natural")]));
  return children.length === 0 ? null : el("technical", {}, children);
}
